/**
 * Common helpers
 * DO NOT IMPORT ANY TYPES ETC FROM ANYWHERE ELSE IN COMMON
 * This will result in many typing errors as this is just a standalone helper file.
 * @param str
 */

const window = require('global/window')

export const validStr = (str?: string) => {
  return str !== null && str !== void 0 && str !== ''
}

/**
 * The size of a string in UTF-8 bytes rather than characters. MySQL column limits are byte based, so
 * a multi-byte character counts for more than its string length (an emoji is four bytes), and
 * `str.length` can pass a check that the write then fails.
 *
 * Counted by hand rather than with TextEncoder/Buffer so it holds everywhere this file is used —
 * neither is a global in every environment (jsdom lacks TextEncoder, browsers lack Buffer).
 * @param str
 */
export const byteLength = (str?: string): number => {
  if (!validStr(str)) {
    return 0
  }

  const value = str as string
  let bytes = 0

  for (let i = 0; i < value.length; i++) {
    const codePoint = value.codePointAt(i) as number

    if (codePoint < 0x80) {
      bytes += 1
    } else if (codePoint < 0x800) {
      bytes += 2
    } else if (codePoint < 0x10000) {
      bytes += 3
    } else {
      bytes += 4
      // Anything above the BMP takes two UTF-16 code units, so step over the low surrogate rather
      // than counting it a second time.
      i++
    }
  }

  return bytes
}

/**
 * The number of characters in a string, counted the way MySQL counts them for a VarChar(n) limit —
 * by character, so an emoji is one. `str.length` counts UTF-16 code units instead, making anything
 * outside the BMP look twice its real size.
 * @param str
 */
export const charLength = (str?: string): number => {
  if (!validStr(str)) {
    return 0
  }

  const value = str as string
  let chars = 0

  for (let i = 0; i < value.length; i++) {
    chars++

    // Anything above the BMP takes two UTF-16 code units, so step over the low surrogate rather
    // than counting it as a second character.
    if ((value.codePointAt(i) as number) >= 0x10000) {
      i++
    }
  }

  return chars
}

/**
 * The string with each run of whitespace in it - tabs, line breaks, non-breaking and other Unicode spaces -
 * made a single space, and none left at either end. A pen name's name is saved and searched like this:
 * names pasted with a space after them were split at that space into one long word, and typing the name
 * never found them [sc-29506]. Anything other than a string comes back as it was.
 * @param str
 */
export function normaliseSpaces (str: string): string
export function normaliseSpaces (str?: string | null): string | null | undefined
export function normaliseSpaces (str?: string | null): string | null | undefined {
  return typeof str === 'string' ? str.replace(/\s+/g, ' ').trim() : str
}

export const validInt = (num?: any) => {
  if (num == null || num == void 0) return false

  let numb = num
  try {
    numb = parseInt(num.toString())
  } catch {
    return false
  }

  return !isNaN(parseInt(numb.toString()))
}

export const validDate = (date: Date | null | undefined): boolean => {
  return !!date && date !== new Date(0)
}

export const isValidLink = (url: string | undefined): boolean => {
  if (!url) {
    return false
  }
  const regex = new RegExp(/^(http|https):\/\/[^ "]+$/gi)
  return regex.test(url)
}

export const dateMinusDays = (days: number, date: Date = newDate()): Date => {
  const useDate = new Date(date)
  useDate.setDate(date.getDate() - days)
  return useDate
}

export const dateAddDays = (days: number, date: Date = newDate()): Date => {
  const useDate = new Date(date)
  useDate.setDate(date.getDate() + days)
  return useDate
}

export const dateAddMonths = (months: number, date: Date = newDate()): Date => {
  const d = new Date(date) // so we don't mutate the existing date
  return new Date(d.setMonth(d.getMonth() + months))
}

export const daysBetweenDates = (from: Date, to: Date): number => {
  const diffInMilliseconds = from.getTime() - to.getTime()
  const days = diffInMilliseconds / (1000 * 60 * 60 * 24)

  return Math.round(days)
}

export const overDueDaysAllowedSinceLaunch = (isOnNewPlan: boolean) => {
  if (isOnNewPlan) {
    return 20
  }

  // const launchDate = new Date(2022, 2, 7)
  // const daysSinceLaunch = new Date() < launchDate ? 0 : daysBetweenDates(launchDate, new Date())

  // return Math.max(Math.floor((50000 * Math.cos(daysSinceLaunch / 40)) + 50000), 150)
  return 150
}

/**
 * Get the date / time in the timezone we're using throughout the system
 * @param value
 */
export const newDate = (value?: number | string | Date): Date => {
  if (value && validInt(value)) {
    if (value.toString().length > 10) {
      value = Math.floor((value as number) / 1000)
    }
  }

  const d = value ? new Date(value) : new Date()
  return d
}

/**
 * Do a straight up conversion of the date passed in it forces the browser to think it's our local time
 * @param date
 */
export const translateDateToCurrentTimezone = (date: string): Date => {
  const hoursDiff = new Date(date).getTimezoneOffset() / 60
  return new Date(Date.parse(new Date(date).toUTCString()) + (hoursDiff * 60 * 60 * 1000))
}

export const dateMinusTime = (date: Date | undefined) => {
  if (!date) {
    return void 0
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
}

export const endOfDay = (date: Date): Date => {
  return new Date(date.setUTCHours(23, 59, 59, 999))
}

export const startOfDay = (date: Date): Date => {
  return new Date(date.setUTCHours(0, 0, 0, 0))
}

// Calendar-day identity in UTC, e.g. for grouping/matching records by day regardless of time-of-day.
export const dateKey = (date: Date | string): string => {
  return new Date(date).toISOString().slice(0, 10)
}

/**
 * `url` with everything from `separator` on replaced by `value`, an object becoming `?a=1&b=2` (props
 * that are undefined are left out).
 *
 * There is deliberately no writer next to it. The client writes the url through vue-router's history
 * (common-app's `useUrlFilters`), because vue-router 4 undoes a url written into the browser's history
 * behind its back — which is why `updateUrlHash` is gone. [sc-28993]
 *
 * With `?` as the separator, the object's own `?` IS the separator. Adding a second one wrote `??a=1`,
 * which `getUrlHash('?')` reads back as a key named `?a` -- so in the Capacitor build, where filters
 * ride on the query string, the first param never came back. [sc-28977]
 */
export const urlWithHash = (url: string, value: any, separator = '#'): string => {
  const index = url.indexOf(separator)
  const base = index === -1 ? url : url.substring(0, index)

  if (typeof value === 'object') {
    const params = Object.keys(value)
      .filter(prop => value[prop] !== void 0)
      .map(prop => `${prop}=${value[prop]}`)
      .join('&')

    return `${base}${separator === '?' ? '' : separator}?${params}`
  }

  return `${base}${separator}${value}`
}

export const getUrlHash = (separator = '#'): any => {
  if (!window.location) { // SSR
    return void 0
  }

  const index = window.location.href.indexOf(separator)
  if (index > -1) {
    const hashIndex = separator !== '#' ? index : index + 1
    const hashData = window.location.href.substring(hashIndex)
    // Not an object
    if (!hashData.startsWith('?')) {
      return hashData === 'null' ? '' : hashData
    } else {
      const allObjectParts = hashData.substring(1).split('&')
      const returnObj: any = {}
      for (const part of allObjectParts) {
        const split = part.split('=')
        returnObj[split[0]] = split[1] === 'null' ? '' : decodeURIComponent(split[1])
      }
      return returnObj
    }
  }

  return void 0
}

export const roundWithPrecision = (value: number, precision: number) => {
  const multiplier = Math.pow(10, precision || 0)
  return Math.round(value * multiplier) / multiplier
}

export const locationOrigin = () => {
  let origin: string

  switch (process.env.BUILD_MODE) {
    case 'alpha': origin = 'https://alpha.booksprout.co'; break
    case 'beta': origin = 'https://beta.booksprout.co'; break
    case 'sandbox': origin = 'https://sandbox.booksprout.co'; break
    case 'master': origin = 'https://booksprout.co'; break
    default: origin = 'https://16e3-82-8-149-134.ngrok.io'; break // Change to whatever ngrok URL we have for dev testing
  }

  return origin
}

export const toCamelCase = (str: string) => {
  return str.replace(/-./g, x=>x[1].toUpperCase())
}

export const ellipsis = (text: string, length: number, ellipsisStr: string = '...', cb: any = void 0) => {
  if (!text) return ''

  if (typeof cb === 'function') {
    cb()
  }

  return text.length > length - ellipsisStr.length
    ? text.substring(0, length - ellipsisStr.length) + ellipsisStr
    : text
}

/**
 * Go through an object recursively and replace any props \\ with / for the URL.
 * @param obj
 */
export const normaliseWebPathsRecursive = (obj: any) => {
  if (!obj) {
    return obj
  }

  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object') {
      normaliseWebPathsRecursive(obj[key])
    } else {
      if (['bookCover', 'amazonBookCover', 'image', 'pdfFile', 'epubFile', 'avatar'].includes(key) && !!obj[key]) {
        obj[key] = obj[key].replace(/\\/g, '/')
      }
    }
  }
}

export const replaceUrlParam = (url: string, paramName: string, paramValue: string) => {
  if (paramValue == null) {
    paramValue = ''
  }
  const pattern = new RegExp('\\b('+paramName+'=).*?(&|#|$)')
  if (url.search(pattern)>=0) {
    return url.replace(pattern,'$1' + paramValue + '$2')
  }
  url = url.replace(/[?#]$/,'')
  return url + (url.indexOf('?')>0 ? '&' : '?') + paramName + '=' + paramValue
}
